const INPUT_REGEX = /[\x20-\x7E\xA0-\xFF]/;

class EngraveDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Find the overlay element (works for both product and cart drawers)
    const overlay = this.querySelector('#EngraveDrawer-Overlay') || this.querySelector('#CartEngraveDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.closeWithNothing.bind(this));
    }
    this.engraveToggleButton = document.querySelector('[data-engrave-button]');

    if (!this.engraveToggleButton) return;

    this.activeElement = null;
    this.formId = window.engraving.productFormId;
    this.hasEngraving = false;
    this.isCartContext = false;
    this.cartItemIndex = null;
    this.productId = null;
    this.variantId = null;

    this.engraveClicked = false;

    // DOM elements
    this.radioButtons = this.querySelectorAll('input[data-font-select-radio]');
    this.fontLineAttribute = this.querySelector('#fontLineAttribute');
    this.engraveAdditionalInfo = this.querySelector('#engraveAdditionalInfo');
    this.submitButton = this.querySelector('[data-add-engraving-submit]');
    this.submitButton.addEventListener('click', this.handleCreateEngravingAttributes.bind(this));
    this.inputsContainer = this.querySelector('.product-form__engraving-input');
    this.casebackTextsDiv = this.querySelector('.product-gallery__caseback-texts');
    this.addLineButton = this.querySelector('[data-add-line-button]');
    this.lineCountSpan = this.querySelector('[data-engraving-line-count]');
    this.removeButton = this.querySelector('[data-engrave-remove-button]');
  }

  connectedCallback() {
    this.handleOpenButtonClick();
    this.initializeEngravingFeature();
    this.handleFontSelection();
  }

  handleFontSelection() {
    // Only initialize font selection if we have radio buttons
    if (this.radioButtons && this.radioButtons.length > 0) {
      this.radioButtons.forEach((radio) => {
        radio.addEventListener('change', (event) => {
          const selectedFont = event.target.value;
          // Update the caseback texts with the selected font
          if (this.casebackTextsDiv) {
            const engravingData =
              window.engraving.engravingFonts[selectedFont] || window.engraving.engravingFonts.DEFAULT;
            this.casebackTextsDiv.style.fontFamily = selectedFont;
            this.casebackTextsDiv.style.fontSize = engravingData.size;
            this.casebackTextsDiv.style.color = window.engravingTextColor;
          }
        });
      });
    }
  }

  handleOpenButtonClick() {
    // Prevent duplicate event listeners

    if (this.openButtonListenerAdded) return;
    this.openButtonListenerAdded = true;

    // Handle all engrave buttons (both product page and cart drawer)
    document.addEventListener('click', (event) => {
      const engraveButton = event.target.closest('[data-engrave-button]');
      if (!engraveButton) return;

      this.engraveClicked = true;

      document.querySelector('.drawer-wrapper .drawer__inner').style.transform = 'translateX(-100%)';
      if (document.querySelector('cart-drawer').classList.contains('active')) {
        document.getElementById('EngraveDrawer-Overlay').classList.add('drawer-overlay-open');
      }

      event.preventDefault();

      // Check if this is a cart context
      if (engraveButton.hasAttribute('data-cart-item-index')) {
        // This is a cart drawer button - use cart engraving drawer
        // Capture values fresh for each click to avoid closure issues
        const cartItemIndex = engraveButton.getAttribute('data-cart-item-index');
        const productId = engraveButton.getAttribute('data-product-id');
        const variantId = engraveButton.getAttribute('data-variant-id');

        // Set class properties
        this.isCartContext = true;
        this.cartItemIndex = cartItemIndex;
        this.productId = productId;
        this.variantId = variantId;
        this.engraveToggleButton = engraveButton;

        // Load product data for cart context, then open
        this.loadProductDataForCart();
      } else {
        // This is a product page button - use product engraving drawer
        this.isCartContext = false;
        this.engraveToggleButton = engraveButton;
        this.open(engraveButton);
      }
    });

    if (this.editButton) {
      this.editButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.submitButton.textContent = 'Update Engraving';
        this.open(this.editButton);
      });
    }

    if (this.removeButton) {
      this.removeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.removeEngraving();
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    document.body.classList.add('overflow-hidden');

    // Save current input values as backup
    this.saveInputValues();

    // Trigger animation and setup
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');

      // Delay displayTexts to ensure inputs are fully created
      setTimeout(() => {
        this.displayTexts();
      }, 400);

      // Setup focus trap after animation
      this.addEventListener('transitionend', this.handleOpenTransitionEnd.bind(this), { once: true });
    });
  }

  handleOpenTransitionEnd() {
    const focusElement = this.querySelector('.drawer__close');
    if (focusElement) {
      trapFocus(this, focusElement);
    }
  }
  closeWithNothing() {
    this.restoreInputValues();
    this.close();
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    document.querySelector('.drawer-wrapper .drawer__inner').removeAttribute('style');
    setTimeout(() => {
      document.getElementById('EngraveDrawer-Overlay').classList.remove('drawer-overlay-open');
    }, 275);
    removeTrapFocus(this.activeElement);
    this.cleanupEmptyInputs();
    this.updateLineCount();
  }

  saveInputValues() {
    const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
    lineInputs.forEach((lineInput) => {
      lineInput.setAttribute('data-val', lineInput.value);
    });
  }

  restoreInputValues() {
    const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
    lineInputs.forEach((lineInput) => {
      lineInput.value = lineInput.getAttribute('data-val') || '';
    });
  }

  cleanupEmptyInputs() {
    const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
    lineInputs.forEach((lineInput, index) => {
      if (lineInput.value === '' && index !== 0) {
        // Remove the entire input container (input + label + div)
        const inputContainer = lineInput.closest('.line-input-container') || lineInput.parentElement.parentElement;
        if (inputContainer) {
          inputContainer.remove();
        }
      }
    });

    // Re-enable add line button if we removed lines
    if (this.addLineButton) {
      this.addLineButton.disabled = false;
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeEngravingFeature() {
    for (let i = 1; i <= window.engraving.initialNumLines; i++) {
      this.createLineInput(i);
    }

    this.addLineButton.addEventListener('click', () => {
      const currentLines = this.inputsContainer.querySelectorAll('[data-engraving-line-input]').length;
      const maxLines = window.engraving.maxNumLines;
      if (currentLines < maxLines) {
        this.createLineInput(currentLines + 1);
      }

      // Disable the add line button if the maximum number of lines is reached
      if (currentLines + 1 >= maxLines) {
        this.addLineButton.disabled = true;
      }
    });
  }

  createLineInput(index = 1) {
    const html = `
      <div class="engraving-input-line" data-engraving-input-line>
        <div class="engraving-input-line__label-container">
          <label for="Line_${index}">Line ${index}</label>
          <span>22 character max</span>
        </div>
        <div class="engraving-input-line__input-container">
          <input type="text" id="Line_${index}"
            placeholder="Line ${index} Text"
            form="${this.formId}"
            maxlength="22"
            autocomplete="off"
            data-engraving-line-input
          >
          <button type="button" aria-label="Remove Line" class="engraving-input-line__remove-button" data-engraving-remove-line>${window.engraving.trashIcon}</button>
          <span class="product-form__engraving-input-error hidden" data-engraving-input-error>You have entered an invalid character(s)</span>
        </div>
      </div>
    `;

    const htmlDom = new DOMParser().parseFromString(html, 'text/html');
    const inputContainer = htmlDom.body.firstChild;
    const input = htmlDom.querySelector('input');

    // Attach change event listener to the input field
    input.addEventListener('input', () => {
      this.checkHasEngraving();
      this.displayTexts();
      this.validateEngraving();
    });

    const removeLineButton = htmlDom.querySelector('[data-engraving-remove-line]');
    removeLineButton &&
      removeLineButton.addEventListener('click', () => {
        // Remove the line input when the button is clicked
        this.inputsContainer.removeChild(inputContainer);
        this.checkHasEngraving();
        this.displayTexts();
        this.validateEngraving();
        this.updateLineCount();

        this.addLineButton.disabled = false;
      });

    this.inputsContainer.appendChild(inputContainer);

    this.updateLineCount();
  }

  checkHasEngraving() {
    const currentLines = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
    const hasEngraving = Array.from(currentLines).some((input) => input.value.trim() !== '');
    this.hasEngraving = hasEngraving;
  }

  updateLineCount() {
    const currentLines = this.inputsContainer.querySelectorAll('[data-engraving-line-input]').length;
    this.lineCountSpan.textContent = currentLines;
  }

  removeEngraving() {
    // Clear all input fields
    const inputLines = this.inputsContainer.querySelectorAll('[data-engraving-input-line]');
    inputLines.forEach((inputLine) => {
      this.inputsContainer.removeChild(inputLine);
    });

    this.createLineInput(1);

    // Clear hidden inputs
    this.fontLineAttribute.innerHTML = '';
    this.engraveAdditionalInfo.innerHTML = '';

    // Handle cart context vs product context
    if (this.isCartContext) {
      this.removeCartItemEngraving();
    } else {
      // Update the add engraving buy box button
      this.engraveToggleButton.classList.remove('engraving-added');

      // Update all engraving buttons for this product across the site
      this.updateAllEngravingButtons(this.productId, false, false);
    }

    // Reset the submit button text
    this.submitButton.disabled = false;
    this.submitButton.textContent = window.engraving.addEngraveLabel;

    // Update state
    this.classList.remove('engrave-drawer--engraving-added');
    this.casebackTextsDiv.innerHTML = '';
    this.hasEngraving = false;
  }

  displayTexts() {
    // Get all input values and concatenate them
    const inputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');

    let text = '';

    inputs.forEach((input, index) => {
      // Use input.value first, but fall back to data-val if value is empty
      const inputValue = input.value || input.getAttribute('data-val') || '';

      // Filter the input value to include only characters matching the regex
      const sanitizedValue = Array.from(inputValue)
        .filter((char) => INPUT_REGEX.test(char))
        .join('');

      if (sanitizedValue) {
        text += '<span>' + sanitizedValue + '</span>';
      }
    });

    // Update the content of the span with the concatenated text
    this.casebackTextsDiv.innerHTML = text;
  }

  validateEngraving() {
    // Get all input values and concatenate them
    const engravingLines = this.querySelectorAll('[data-engraving-input-line]');
    let hasError = false;

    engravingLines.forEach((line) => {
      const input = line.querySelector('input');
      const errorMessageSpan = line.querySelector('[data-engraving-input-error]');
      const inputValue = input.value;
      for (let i = 0; i < inputValue.length; i++) {
        const key = inputValue[i];
        if (!INPUT_REGEX.test(key)) {
          input.parentElement.classList.add('error');
          errorMessageSpan.classList.remove('hidden');
          hasError = true;
          break;
        } else {
          input.parentElement.classList.remove('error');
          errorMessageSpan.classList.add('hidden');
        }
      }
    });

    this.hasValidEngraving = !hasError;

    this.submitButton.toggleAttribute('disabled', !this.hasValidEngraving || !this.hasEngraving);
  }

  createHiddenInput(name, value) {
    const input = document.createElement('input');
    input.setAttribute('name', `properties[${name}]`);
    input.setAttribute('type', 'hidden');
    input.setAttribute('form', this.formId);
    input.setAttribute('value', value);
    return input;
  }

  handleCreateEngravingAttributes() {
    const activeFontRadio = this.querySelector('input[data-font-select-radio]:checked');

    // Clear previous attributes before appending new ones
    this.fontLineAttribute.innerHTML = '';
    this.engraveAdditionalInfo.innerHTML = '';

    // If no engraving text is present, do not proceed
    if (!this.hasEngraving) return;

    // Get the value of the active radio button
    const selectedFont = activeFontRadio.value;
    const engravingData = window.engraving.engravingFonts[selectedFont] || window.engraving.engravingFonts.DEFAULT;
    const engraveSize = engravingData.engraving_size;
    const fontSize = engravingData.size;

    // Apply styles to casebackTexts
    if (this.casebackTextsDiv) {
      this.casebackTextsDiv.style.fontFamily = selectedFont;
      this.casebackTextsDiv.style.fontSize = fontSize;
      this.casebackTextsDiv.style.color = window.engravingTextColor;
    }

    // Add line properties first (in correct order)
    const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
    lineInputs.forEach((lineInput, index) => {
      if (lineInput.value != '') {
        this.fontLineAttribute.appendChild(this.createHiddenInput(`personalization_line${index + 1}`, lineInput.value));
        lineInput.setAttribute('data-val', lineInput.value);

        // Only update DD elements in cart drawer if we're in cart context
        if (this.isCartContext) {
          const lineNumber = index + 1;
          const ddId = `engraving-line-${lineNumber}-item-${this.cartItemIndex || 'unknown'}`;
          const ddElement = document.querySelector(`#${ddId}`);

          if (ddElement) {
            ddElement.textContent = lineInput.value;
          }
        }
      }
    });

    // Add font properties after line properties
    this.fontLineAttribute.appendChild(this.createHiddenInput('font_style', selectedFont));
    this.fontLineAttribute.appendChild(this.createHiddenInput('font_size', engraveSize));
    const engravingPrice =
      document.querySelector('.product__add-engraving-cta')?.dataset.engravePrice || window.engraving.engravePrice;

    // Convert from cents to dollars since cart drawer template multiplies by 100
    this.engraveAdditionalInfo.appendChild(
      // this.createHiddenInput('engraving_fee', window.engraving.engravePrice / 100)

      this.createHiddenInput('engraving_fee', `${engravingPrice}`)
    );
    this.engraveAdditionalInfo.appendChild(this.createHiddenInput('_LPROP', 'Final Sale'));

    // Add timestamp
    const engravingDate = new Date();
    const timeStamp = engravingDate.toISOString();
    this.engraveAdditionalInfo.appendChild(this.createHiddenInput('_time_stamp', timeStamp));

    // Generate engraving_id and add as hidden input
    const engravingId = `${engravingDate.getTime()}${selectedFont.length}${engraveSize.length}`;
    this.engraveAdditionalInfo.appendChild(this.createHiddenInput('_engraving_id', engravingId));

    // Only update cart item if we're in cart context
    if (this.isCartContext) {
      this.updateCartItemFromCartContext();
    }

    // Handle cart context vs product context
    if (!this.isCartContext) {
      this.engraveToggleButton.classList.add('engraving-added');
      this.engraveToggleButton.querySelector('.text-update').textContent = 'Update Engraving';
    }

    // Update the submit button text
    this.submitButton.textContent = 'Update Engraving';

    this.classList.add('engrave-drawer--engraving-added');
  }

  ensureInputsCreated() {
    // Check if inputs already exist
    const existingInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');

    // If no inputs exist, create them
    if (existingInputs.length === 0) {
      for (let i = 1; i <= window.engraving.initialNumLines; i++) {
        this.createLineInput(i);
      }
    }
  }

  async loadProductDataForCart() {
    try {
      // Clear any existing engraving data first to prevent cross-contamination
      this.existingLines = {};
      this.hasEngraving = false;

      // Clear any existing input values
      const existingInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
      existingInputs.forEach((input) => {
        input.value = '';
      });

      // Get product data from button data attributes
      const rawEngravePrice = this.engraveToggleButton.getAttribute('data-engrave-price');
      const productData = {
        title: this.engraveToggleButton.getAttribute('data-product-title'),
        engrave_price: parseFloat(rawEngravePrice) || 0,
        variant_sku: this.engraveToggleButton.getAttribute('data-variant-sku') || 'DEFAULT',
        engrave_image: this.engraveToggleButton.getAttribute('data-engrave-image'),
      };

      // If no engraving image from data attributes, try to fetch from product API
      if (!productData.engrave_image) {
        try {
          const response = await fetch(`/products/${this.productId}.js`);
          if (response.ok) {
            const product = await response.json();
            const imageSubstr = 'custom';
            for (const media of product.media) {
              // Check different possible properties for the image URL
              let imageUrl = null;
              if (media.src && media.src.includes(imageSubstr)) {
                imageUrl = media.src;
              } else if (
                media.preview_image &&
                media.preview_image.src &&
                media.preview_image.src.includes(imageSubstr)
              ) {
                imageUrl = media.preview_image.src;
              } else if (media.alt && media.alt.toLowerCase().includes('custom')) {
                // Fallback: if alt text contains 'custom', use the media URL
                imageUrl = media.preview_image ? media.preview_image.src : media.src;
              }

              if (imageUrl) {
                productData.engrave_image = imageUrl;
                break;
              }
            }
          }
        } catch (error) {}
      }

      if (window.updateEngravingDrawer) {
        window.updateEngravingDrawer(productData);
      } else {
        console.error('updateEngravingDrawer function not found!');
      }

      // Ensure inputs are created first
      this.ensureInputsCreated();

      // Only load existing engraving data if we haven't just synced from another context
      // This prevents overwriting freshly synced data
      if (!this._justSynced) {
        this.loadExistingEngravingData();
      } else {
        this._justSynced = false; // Reset the flag
      }

      // Now open the engraving drawer using the open() function
      this.open(this.engraveToggleButton);
    } catch (error) {
      console.error('Error loading product data for cart:', error);
    }
  }

  async loadExistingEngravingData() {
    try {
      // Don't load if we're in the middle of a sync operation
      if (window._engravingSyncInProgress) {
        return;
      }

      // Also check if this drawer was just synced
      if (this._justSynced) {
        this._justSynced = false; // Reset the flag
        return;
      }

      // Add a small delay to ensure we get the latest cart data
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get the cart data to find existing engraving properties
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        // Shopify cart items are 1-based, but we need to find by line number
        const cartItem = cart.items.find((item) => item.index === parseInt(this.cartItemIndex));

        // If not found by index, try by line number (which is 1-based)
        const cartItemByLine = cart.items[parseInt(this.cartItemIndex) - 1];
        const targetCartItem = cartItem || cartItemByLine;

        if (targetCartItem && targetCartItem.properties) {
          // Check if this item has engraving properties
          const hasEngraving = Object.keys(targetCartItem.properties).some(
            (key) =>
              key.startsWith('personalization_line') &&
              targetCartItem.properties[key] &&
              targetCartItem.properties[key].trim() !== ''
          );

          if (hasEngraving) {
            // Check if the data is already correct before loading
            const currentInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
            let dataAlreadyCorrect = true;

            if (currentInputs.length > 0) {
              currentInputs.forEach((input, index) => {
                const lineKey = `personalization_line${index + 1}`;
                const cartValue = targetCartItem.properties[lineKey];
                const currentValue = input.value || input.getAttribute('data-val') || '';

                if (cartValue && cartValue !== currentValue) {
                  dataAlreadyCorrect = false;
                }
              });
            }

            if (dataAlreadyCorrect && currentInputs.length > 0) {
              return;
            }

            // Wait a bit for DOM to be ready
            setTimeout(() => {
              // First, determine how many lines we need based on existing data
              const maxLines = Math.max(
                1,
                Object.keys(targetCartItem.properties).filter(
                  (key) =>
                    key.startsWith('personalization_line') &&
                    targetCartItem.properties[key] &&
                    targetCartItem.properties[key].trim() !== ''
                ).length
              );

              // Create additional input fields if needed
              const currentInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');

              for (let i = currentInputs.length; i < maxLines; i++) {
                this.createLineInput(i + 1);
              }

              // Now populate all input fields with existing engraving data
              const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');

              lineInputs.forEach((input, index) => {
                const lineKey = `personalization_line${index + 1}`;
                const cartValue = targetCartItem.properties[lineKey];
                const currentValue = input.value || input.getAttribute('data-val') || '';

                // Only update if the values are different AND the current value isn't more complete
                // This prevents overwriting newer user input with older cart data
                if (cartValue && cartValue !== currentValue) {
                  // Check if current value is more complete (longer) than cart value
                  if (currentValue.length <= cartValue.length) {
                    input.value = cartValue;
                    input.setAttribute('data-val', cartValue);
                  }
                }
              });

              // Set the font style if it exists
              if (targetCartItem.properties.font_style) {
                const fontRadio = this.querySelector(`input[value="${targetCartItem.properties.font_style}"]`);
                if (fontRadio) {
                  fontRadio.checked = true;

                  // Update the caseback texts with the selected font
                  if (this.casebackTextsDiv && window.engraving && window.engraving.engravingFonts) {
                    const engravingData =
                      window.engraving.engravingFonts[targetCartItem.properties.font_style] ||
                      window.engraving.engravingFonts.DEFAULT;
                    this.casebackTextsDiv.style.fontFamily = targetCartItem.properties.font_style;
                    this.casebackTextsDiv.style.fontSize = engravingData.size;
                    this.casebackTextsDiv.style.color = window.engravingTextColor || 'black';
                  }
                }
              }

              // Update the button state to show "Edit Engraving"
              this.engraveToggleButton.classList.add('engraving-added');
              this.submitButton.textContent = 'Update Engraving';
              this.classList.add('engrave-drawer--engraving-added');
              this.hasEngraving = true;

              // Display the loaded text in the preview area
              this.displayTexts();
            }, 100);
          }
        }
      }
    } catch (error) {
      console.error('Error loading existing engraving data:', error);
    }
  }

  /**
   * Updates a cart item with engraving properties
   * @param {string|Object} cartItemOrKey - Either a cart item key (string) or cart item object
   * @param {number} [arrayIndex] - Array index (optional, used when cartItemOrKey is an object)
   * @param {string} productId - Product ID
   * @param {boolean} isCartContext - Whether this is being called from cart context
   */
  async updateCartItem(cartItemOrKey, arrayIndex, productId, isCartContext = false) {
    try {
      // Extract cart item key from either string or object
      const cartItemKey = typeof cartItemOrKey === 'string' ? cartItemOrKey : cartItemOrKey.key;

      // Collect all engraving properties
      const properties = {};

      // Get line inputs
      const lineInputs = this.inputsContainer.querySelectorAll('[data-engraving-line-input]');
      lineInputs.forEach((lineInput, index) => {
        if (lineInput.value.trim() !== '') {
          properties[`personalization_line${index + 1}`] = lineInput.value.trim();
        }
      });

      // Get font properties
      const activeFontRadio = this.querySelector('input[data-font-select-radio]:checked');
      if (activeFontRadio) {
        const selectedFont = activeFontRadio.value;
        const engravingData = window.engraving.engravingFonts[selectedFont] || window.engraving.engravingFonts.DEFAULT;

        properties.font_style = selectedFont;
        properties.font_size = engravingData.engraving_size;
        // Convert from cents to dollars since cart drawer template multiplies by 100
        // properties.engraving_fee = window.engraving.engravePrice / 100;
        properties.engraving_fee = window.engraving.engravePrice > 0 ? `$${window.engraving.engravePrice}.00` : '0';
        properties._LPROP = 'Final Sale';

        // Add timestamp
        const engravingDate = new Date();
        properties._time_stamp = engravingDate.toISOString();
        properties._engraving_id = `${engravingDate.getTime()}${selectedFont.length}${
          engravingData.engraving_size.length
        }`;
      }

      if (this.engraveClicked) sessionStorage.setItem('engraving_added', true);

      // Update cart item using the cart item key
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: cartItemKey,
          properties: properties,
        }),
      });

      if (response.ok) {
        // Check if there are any engraving properties
        const hasEngravingProperties = Object.keys(properties).some(
          (key) => key.startsWith('personalization_line') && properties[key]?.trim() !== ''
        );

        // Update the button state
        if (hasEngravingProperties) {
          this.engraveToggleButton.classList.add('engraving-added');
        } else {
          this.engraveToggleButton.classList.remove('engraving-added');
        }

        // Update all engraving buttons for this product across the site
        this.updateAllEngravingButtons(productId, hasEngravingProperties, isCartContext);

        // Update cart item details with engraving text (only in cart context)
        if (isCartContext) {
          this.updateCartItemEngravingText(properties);
        }

        // Handle context-specific actions
        if (isCartContext) {
          // Refresh cart drawer and close drawer for cart context
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.refresh) {
            cartDrawer.refresh();
          }
          this.close();
        }
      } else {
        console.error('Failed to update cart item:', await response.text());
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
    }
  }

  async removeCartItemEngraving() {
    try {
      // Get cart data to find the cart item key
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const cartItem = cart.items.find((item) => item.variant_id === parseInt(this.variantId));

      if (!cartItem) {
        console.error('Cart item not found for engraving removal');
        return;
      }

      // Remove engraving properties from cart item by setting them to null
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: cartItem.key,
          properties: {
            'Line 1': null,
            'Line 2': null,
            'Line 3': null,
            Font: null,
            'Font Size': null,
            engraving_fee: null,
            _LPROP: null,
            _time_stamp: null,
            _engraving_id: null,
          },
        }),
      });

      if (response.ok) {
        // Update the button state
        this.engraveToggleButton.classList.remove('engraving-added');

        // Update all engraving buttons for this product across the site
        this.updateAllEngravingButtons(this.productId, false, true);

        // Remove engraving text from cart item details
        this.updateCartItemEngravingText({});

        // Refresh cart drawer
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.refresh) {
          cartDrawer.refresh();
        }

        // Close the drawer
        this.close();
      } else {
        console.error('Failed to remove engraving from cart item');
      }
    } catch (error) {
      console.error('Error removing engraving from cart item:', error);
    }
  }

  updateCartItemEngravingText(properties) {
    // Find the cart item container for this specific item using the cart item index
    let cartItemContainer;

    if (this.cartItemIndex) {
      // Use the specific cart item index to target the exact item
      // Try multiple selectors to find the correct cart item container
      cartItemContainer =
        document.querySelector(`#CartDrawer-Item-${this.cartItemIndex}`) ||
        document.querySelector(`#CartItem-${this.cartItemIndex}`) ||
        document.querySelector(`[data-cart-item-index="${this.cartItemIndex}"]`)?.closest('.cart-item') ||
        document.querySelector(`[data-cart-item-index="${this.cartItemIndex}"]`)?.closest('tr');
    } else {
      // Fallback to closest method if no index available
      cartItemContainer = this.engraveToggleButton.closest('.cart-item');
    }

    if (!cartItemContainer) {
      console.warn('Cart item container not found for engraving text update');
      return;
    }

    // Find the cart item details container
    const cartItemDetails = cartItemContainer.querySelector('.cart-item__details');
    if (!cartItemDetails) {
      console.warn('Cart item details container not found for engraving text update');
      return;
    }

    // Clear existing engraving options first
    const existingOptions = cartItemDetails.querySelectorAll('.product-option');
    existingOptions.forEach((option) => {
      const optionText = option.textContent.toLowerCase();
      if (
        optionText.includes('engraving') ||
        optionText.includes('line') ||
        optionText.includes('font') ||
        optionText.includes('fee')
      ) {
        option.remove();
      }
    });

    // If no properties provided, just clear and return
    if (!properties || Object.keys(properties).length === 0) {
      return;
    }

    // Add engraving properties in the correct order: Lines first, then Font, then Fee
    // 1. Engraving lines in order (1, 2, 3) - matching desired order
    for (let i = 1; i <= 3; i++) {
      const lineKey = `personalization_line${i}`;
      if (properties[lineKey] && properties[lineKey].trim() !== '') {
        const optionElement = document.createElement('div');
        optionElement.className = 'product-option';

        const dt = document.createElement('dt');
        dt.textContent = `Line ${i}: `;

        const dd = document.createElement('dd');
        // Use a unique ID that includes the cart item index to avoid conflicts
        dd.id = `engraving-line-${i}-item-${this.cartItemIndex || 'unknown'}`;
        dd.textContent = properties[lineKey];

        optionElement.appendChild(dt);
        optionElement.appendChild(dd);
        cartItemDetails.appendChild(optionElement);
      }
    }

    // 2. Font style
    if (properties.font_style) {
      const fontOption = document.createElement('div');
      fontOption.className = 'product-option';

      const dt = document.createElement('dt');
      dt.textContent = 'Font: ';

      const dd = document.createElement('dd');
      dd.textContent = properties.font_style;

      fontOption.appendChild(dt);
      fontOption.appendChild(dd);
      cartItemDetails.appendChild(fontOption);
    }

    // 3. Engraving fee
    // const engravingFee = parseFloat(properties.engraving_fee) || 0;
    const engravingFee = properties.engraving_fee.replace('.00', '') || '0';
    // if (engravingFee !== null) {
    const feeOption = document.createElement('div');
    feeOption.className = 'product-option';

    const dt = document.createElement('dt');
    dt.textContent = 'Engraving fee: ';

    const dd = document.createElement('dd');
    // const formattedFee = engravingFee > 0 ? `$${engravingFee * 100}` : 'Free';
    console.log(engravingFee, '<---- engravingFee');
    const formattedFee = engravingFee === '0' ? 'Free' : engravingFee;
    dd.textContent = formattedFee;

    feeOption.appendChild(dt);
    feeOption.appendChild(dd);
    cartItemDetails.appendChild(feeOption);
    // }
  }

  async updateCartItemFromCartContext() {
    try {
      // Get cart data to find the cart item key
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        // Find cart item with this line index (more precise when multiple items have same variant ID)
        // Note: data-cart-item-index is "item.index + 1", so we subtract 1 to get actual index
        const actualCartIndex = this.cartItemIndex ? parseInt(this.cartItemIndex) - 1 : 0;
        const cartItem = cart.items.find((item, i) => i === actualCartIndex);

        if (cartItem) {
          const arrayIndex = cart.items.indexOf(cartItem);
          await this.updateCartItem(cartItem, arrayIndex, cartItem.product_id, true); // true = cart context
        } else {
          console.error('Cart item not found for cart context update');
        }
      }
    } catch (error) {
      console.error('Error updating cart item from cart context:', error);
    }
  }

  async updateAllEngravingButtons(productId, hasEngraving, isCartContext = false) {
    // Update PDP engraving button
    const pdpButton = document.querySelector('[data-engrave-button]:not([data-cart-item-index])');
    if (pdpButton) {
      if (hasEngraving) {
        pdpButton.classList.add('engraving-added');
      } else {
        pdpButton.classList.remove('engraving-added');
      }
    }

    // Only update cart drawer buttons when in cart context
    if (isCartContext) {
      // Update only the specific cart item button being edited
      const specificCartButton = document.querySelector(
        `[data-engrave-button][data-cart-item-index="${this.cartItemIndex}"]`
      );
      if (specificCartButton) {
        if (hasEngraving) {
          specificCartButton.classList.add('engraving-added');
        } else {
          specificCartButton.classList.remove('engraving-added');
        }
      }
    }
  }
}

customElements.define('engrave-drawer', EngraveDrawer);
